'use client';

import { useAuth } from '@/backend/context';
import { useOwner } from '@/backend/firebase/hooks';
import { useSlug } from '@/backend/firebase/hooks/useSlug';
import { useStorage } from '@/backend/firebase/hooks/useStorage';
import { FormGuard } from '@/components/form-guard';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/ui/PageHeader';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { SocialMediaIcons } from '@/lib/paths';
import { cn } from '@/lib/utils';
import { type BrandingDataWithoutSlug } from '@/shared/schema';
import {
  Camera,
  Copy,
  Crop,
  Facebook,
  Globe,
  ImageIcon,
  Instagram,
  Loader2,
  MapPin,
  Navigation,
  Plus,
  Trash2,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

import { InternationalPhoneInput } from '@/components/phone-input';
import { ImageCropperModal } from '@/components/image-cropper-modal';
import { IMAGE_RATIOS } from '@/shared/utils';

interface SlugForm {
  slug: string;
}

/**
 * Extracts a username from a social media URL or returns the input if it's already a username.
 */
const extractUsername = (
  input: string,
  platform: 'instagram' | 'tiktok',
): string => {
  if (!input) return '';
  let value = input.trim();

  // If it doesn't look like a URL, just clean the @
  if (!value.includes('http') && !value.includes('.com')) {
    return value.replace(/^@/, '');
  }

  try {
    const url = new URL(value.startsWith('http') ? value : `https://${value}`);
    const pathParts = url.pathname.split('/').filter((p) => p.length > 0);

    if (pathParts.length > 0) {
      return pathParts[0];
    }
  } catch (e) {
    // If URL parsing fails, return original trimmed value
  }

  return value;
};

export default function BrandingSettingsPage() {
  const ownerId = useAuth((s) => s.user?.uid);
  const profileStatus = useAuth((s) => s.status);
  const refreshProfile = useAuth((s) => s.refreshProfile);
  const customDomain = useAuth((s) => s.publicDoc?.meta?.customDomain);
  const { data: profile } = useOwner.getById(ownerId);

  const uploadMutation = useStorage.useUploadBranding();
  const slugMutation = useSlug.create();
  const profileMutation = useOwner.updatePublic();

  const isFirstRender = useRef(true);

  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);

  // --- CROPPER STATE ---
  const [cropper, setCropper] = useState<{
    isOpen: boolean;
    imageSrc: string | null;
    aspectRatio: number;
    type: 'logo' | 'banners';
  }>({
    isOpen: false,
    imageSrc: null,
    aspectRatio: 1,
    type: 'logo',
  });

  const handleCopyCustomDomain = () => {
    if (customDomain) {
      navigator.clipboard.writeText(`https://${customDomain}`);
      toast.success('¡Dominio personalizado copiado!');
    }
  };

  const {
    register,
    control,
    setValue,
    getValues,
    reset,
    formState: { isDirty: isFormDirty },
  } = useForm<BrandingDataWithoutSlug>();

  const {
    watch: watchForSlug,
    register: registerSlug,
    reset: resetSlug,
    formState: { isDirty: isSlugDirty },
  } = useForm<SlugForm>();

  // 2. Sincronización Inicial
  useEffect(() => {
    const branding = profile?.public?.branding;
    if (branding && isFirstRender.current) {
      isFirstRender.current = false;

      resetSlug({ slug: branding.slug ?? '' });

      reset({
        name: branding.name ?? '',
        profession: branding.profession ?? '',
        description: branding.description ?? '',
        logoUrl: branding.logoUrl ?? '',
        bannerImage: branding.bannerImage ?? '',
        styles: {
          primary: branding.styles?.primary ?? '#0F172A',
          theme: branding.styles?.theme ?? 'light',
        },
        contact: {
          phone: branding.contact?.phone ?? '',
          heroWhatsappMessage: branding.contact?.heroWhatsappMessage ?? '',
          instagram: branding.contact?.instagram ?? '',
          facebook: branding.contact?.facebook ?? '',
          tiktok: branding.contact?.tiktok ?? '',
          address: branding.contact?.address ?? '',
          google_maps: branding.contact?.google_maps ?? '',
        },
      });
    }
  }, [profile, reset, resetSlug]);

  const formValues = useWatch({ control });
  const watchSlug = watchForSlug('slug') || '';
  const watchLogo = formValues.logoUrl || '';

  const currentSavedSlug = profile?.public?.branding?.slug || '';
  const isCurrentSlug =
    watchSlug.toLowerCase() === currentSavedSlug.toLowerCase() &&
    watchSlug.length > 0;
  const { data: isAvailable, isLoading: isCheckingSlug } =
    useSlug.checkAvailability(watchSlug);
  const isSlugValidLength = watchSlug.length >= 3;
  const isSlugReadyToSave =
    isSlugValidLength && !isCurrentSlug && isAvailable === true;

  const hasChanges = isFormDirty || isSlugDirty;

  const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});

  const handleImgError = (key: string) => {
    setImgErrors((prev) => ({ ...prev, [key]: true }));
  };

  const handleSaveAll = async () => {
    if (!ownerId) return;
    try {
      const promises = [];
      const slugChanged = isSlugDirty && isSlugReadyToSave;

      if (slugChanged) promises.push(slugMutation.mutateAsync(watchSlug));

      if (isFormDirty) {
        const existingViewMode =
          (profile?.public?.branding as any)?.viewMode ||
          (profile?.public?.meta as any)?.viewMode;

        promises.push(
          profileMutation.mutateAsync({
            branding: {
              ...formValues,
              ...(existingViewMode ? { viewMode: existingViewMode } : {}),
            } as any,
          }),
        );
      }

      await Promise.all(promises);

      // Invalidate global auth context
      await refreshProfile();

      // Reset baselines for both forms to clear isDirty
      const currentValues = getValues();
      reset(currentValues);
      resetSlug({ slug: watchSlug });

      toast.success('¡Identidad de marca actualizada!');

      // If slug changed, reload the full page to refresh everything
      if (slugChanged) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      toast.error('Hubo un error al guardar');
    }
  };

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'logo' | 'banners',
  ) => {
    const file = e.target.files?.[0];
    if (!file || !ownerId) return;

    // Trigger local loading feedback immediately for the area
    if (type === 'logo') setIsUploadingLogo(true);
    else setIsUploadingBanner(true);

    const reader = new FileReader();
    reader.onload = () => {
      setCropper({
        isOpen: true,
        imageSrc: reader.result as string,
        aspectRatio: type === 'logo' ? IMAGE_RATIOS.SQUARE : IMAGE_RATIOS.WIDE,
        type,
      });
    };
    reader.readAsDataURL(file);
    // Reset input
    e.target.value = '';
  };

  const onCropComplete = async (croppedFile: File) => {
    if (!ownerId) return;

    const { type } = cropper;
    // 1. Close immediately
    setCropper((prev) => ({ ...prev, isOpen: false }));

    // 2. Background upload process
    const uploadProcess = async () => {
      if (type === 'logo') setIsUploadingLogo(true);
      else setIsUploadingBanner(true);

      try {
        const url = await uploadMutation.mutateAsync({
          file: croppedFile,
          ownerId,
          type: type === 'logo' ? 'logo' : 'banners',
          ratioType: type === 'logo' ? 'SQUARE' : 'WIDE',
        });

        if (type === 'logo') {
          setValue('logoUrl', url, { shouldDirty: true });
        } else {
          setValue('bannerImage', url, { shouldDirty: true });
        }
        toast.success('Imagen cargada con éxito.');
      } catch (error) {
        toast.error('Error al subir imagen');
      } finally {
        setIsUploadingLogo(false);
        setIsUploadingBanner(false);
      }
    };

    uploadProcess();
  };

  if (profileStatus === 'loading' || !profile) {
    return (
      <div className="flex h-[80vh] w-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-10 w-10 animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen pb-32">
      <main className="relative mx-auto max-w-md space-y-6 px-4 pt-6">
        {/* HEADER */}
        <PageHeader 
          title="Identidad de Marca" 
          subtitle="Configura tu perfil público" 
          showBack={true} 
        />

        {/* HERO SECTION */}
        <div className="relative mb-14">
          <div className="border-card bg-secondary/30 relative aspect-video overflow-hidden rounded-[2.5rem] border-4 shadow-2xl transition-all">
            {formValues.bannerImage ? (
              <div className="group relative h-full w-full">
                <img
                  src={formValues.bannerImage}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  alt="Banner"
                />

                {/* LOADING OVERLAY */}
                {isUploadingBanner && (
                  <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-slate-950/60 backdrop-blur-sm">
                    <Loader2 className="mb-2 h-10 w-10 animate-spin text-white" />
                    <span className="text-[10px] font-black tracking-widest text-white uppercase">
                      Subiendo Portada...
                    </span>
                  </div>
                )}

                {/* ACTIONS OVERLAY */}
                {!isUploadingBanner && (
                  <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-950/40 transition-opacity group-hover:opacity-100 sm:opacity-0">
                    <div className="flex gap-2 p-4">
                      <label className="cursor-pointer">
                        <div className="flex items-center gap-2 rounded-xl bg-white/90 px-4 py-2.5 text-[10px] font-black text-slate-950 uppercase shadow-2xl backdrop-blur-md transition-transform active:scale-95">
                          <ImageIcon className="h-4 w-4 text-violet-600" />
                          Cambiar
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => {
                            setIsUploadingBanner(true);
                            handleImageUpload(e, 'banners');
                          }}
                        />
                      </label>
                      <Button
                        type="button"
                        variant="secondary"
                        className="h-10 gap-2 rounded-xl bg-white/90 px-4 text-[10px] font-black text-slate-950 uppercase shadow-2xl backdrop-blur-md transition-transform hover:bg-white active:scale-95"
                        onClick={(e) => {
                          e.preventDefault();
                          if (!formValues.bannerImage) return;
                          setCropper({
                            isOpen: true,
                            imageSrc: formValues.bannerImage,
                            aspectRatio: IMAGE_RATIOS.WIDE,
                            type: 'banners',
                          });
                        }}
                      >
                        <Crop className="h-4 w-4 text-amber-600" />
                        Recortar
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        className="h-10 gap-2 rounded-xl px-4 text-[10px] font-black uppercase shadow-2xl active:scale-95"
                        onClick={(e) => {
                          e.preventDefault();
                          setValue('bannerImage', '', { shouldDirty: true });
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                        Quitar
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <label className="flex h-full w-full cursor-pointer flex-col items-center justify-center transition-all hover:bg-slate-100/50">
                {isUploadingBanner ? (
                  <Loader2 className="mb-2 h-10 w-10 animate-spin text-violet-500" />
                ) : (
                  <div className="mb-3 rounded-2xl bg-white p-4 shadow-xl transition-transform duration-500 group-hover:scale-110">
                    <ImageIcon className="h-8 w-8 text-violet-500" />
                  </div>
                )}
                <span className="text-[10px] font-black tracking-widest text-slate-900 uppercase">
                  {isUploadingBanner ? 'Subiendo...' : 'Subir Foto de Portada (16:9)'}
                </span>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => {
                    setIsUploadingBanner(true);
                    handleImageUpload(e, 'banners');
                  }}
                />
              </label>
            )}
          </div>

          <div className="absolute -bottom-10 left-1/2 z-20 -translate-x-1/2">
            <div className="group relative">
              <label className="relative block cursor-pointer">
                <Avatar className="border-background bg-card h-24 w-24 overflow-hidden border-[6px] shadow-xl">
                  <AvatarImage
                    src={watchLogo}
                    className="object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <AvatarFallback>
                    <Camera className="h-8 w-8 opacity-20" />
                  </AvatarFallback>

                  {/* LOGO LOADING OVERLAY */}
                  {isUploadingLogo && (
                    <div className="absolute inset-0 z-30 flex items-center justify-center bg-slate-950/60 backdrop-blur-[2px]">
                      <Loader2 className="h-6 w-6 animate-spin text-white" />
                    </div>
                  )}
                </Avatar>
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    setIsUploadingLogo(true);
                    handleImageUpload(e, 'logo');
                  }}
                  accept="image/*"
                />
              </label>

              {!isUploadingLogo && (
                <label className="absolute right-0 bottom-0 z-40 cursor-pointer">
                  <div className="border-background bg-primary text-primary-foreground flex h-8 w-8 items-center justify-center rounded-full border-2 shadow-lg transition-transform hover:scale-110">
                    <Plus className="h-4 w-4" />
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        setIsUploadingLogo(true);
                        handleImageUpload(e, 'logo');
                      }}
                      accept="image/*"
                    />
                  </div>
                </label>
              )}
            </div>
          </div>
        </div>

        {/* ENLACE PÚBLICO */}
        <div className="space-y-4">
          {customDomain ? (
            <Card className="rounded-[2rem] border border-emerald-500 bg-emerald-50/30 transition-all">
              <CardContent className="space-y-3 p-5">
                <Label className="text-muted-foreground ml-1 text-[9px] font-black tracking-widest uppercase">
                  Mi Dominio Personalizado
                </Label>
                <button
                  onClick={handleCopyCustomDomain}
                  className="bg-background ring-ring flex h-14 w-full items-center justify-between rounded-2xl border px-4 transition-all focus-within:ring-2 active:scale-[0.98]"
                >
                  <div className="flex items-center gap-2 truncate pr-4">
                    <Globe className="h-4 w-4 text-emerald-500" strokeWidth={2.5} />
                    <span className="truncate text-sm font-black tracking-tight text-zinc-900">
                      {customDomain}
                    </span>
                  </div>
                  <Copy className="h-4 w-4 text-zinc-400" strokeWidth={2.5} />
                </button>
              </CardContent>
            </Card>
          ) : (
            <Card
              className={cn(
                'rounded-[2rem] border transition-all',
                isSlugReadyToSave
                  ? 'border-emerald-500 bg-emerald-50/30'
                  : 'bg-card',
              )}
            >
              <CardContent className="space-y-3 p-5">
                <Label className="text-muted-foreground ml-1 text-[9px] font-black tracking-widest uppercase">
                  Mi Enlace (Web)
                </Label>
                <div className="bg-background ring-ring flex h-14 items-center rounded-2xl border px-4 transition-all focus-within:ring-2">
                  <Globe className="text-muted-foreground mr-2 h-4 w-4" />
                  <span className="text-muted-foreground text-xs font-bold">
                    tufirma.pro/
                  </span>
                  <input
                    {...registerSlug('slug')}
                    className="ml-0.5 flex-1 border-none bg-transparent text-sm font-black lowercase outline-none"
                    placeholder="tu-marca"
                  />
                  {isCheckingSlug && (
                    <Loader2 className="h-4 w-4 animate-spin opacity-40" />
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>


        {/* DATOS DE MARCA */}
        <Card className="bg-card rounded-[2rem] border-none shadow-sm">
          <CardContent className="space-y-5 p-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground text-[9px] font-black tracking-widest uppercase">
                  Nombre
                </Label>
                <Input
                  {...register('name')}
                  className="bg-secondary/30 h-14 rounded-2xl border-none text-sm font-black"
                  placeholder="Nombre de tu Marca"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground text-[9px] font-black tracking-widest uppercase">
                  Especialidad
                </Label>
                <Input
                  {...register('profession')}
                  className="bg-secondary/30 h-14 rounded-2xl border-none text-sm font-bold tracking-tighter uppercase"
                  placeholder="Colorista y Estilista"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground text-[9px] font-black tracking-widest uppercase">
                  Descripción
                </Label>
                <Textarea
                  {...register('description')}
                  className="bg-secondary/30 min-h-[100px] rounded-2xl border-none text-sm font-medium"
                  placeholder="Cuéntales qué te hace único..."
                />
              </div>
            </div>

            <Separator className="opacity-50" />

            {/* SECCIÓN DE CONEXIONES */}
            <div className="space-y-4">
              <Label className="text-foreground flex items-center gap-2 text-[10px] font-black tracking-[0.2em] uppercase">
                <Globe className="text-primary h-4 w-4" /> Redes y Contacto
              </Label>

              {/* Teléfono con Componente Especial */}
              <div className="space-y-2">
                <Label className="text-muted-foreground ml-1 text-[9px] font-black tracking-widest uppercase">
                  WhatsApp / Teléfono de Contacto
                </Label>
                <Controller
                  control={control}
                  name="contact.phone"
                  render={({ field }) => (
                    <InternationalPhoneInput
                      value={field.value || ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              </div>

              {/* Mensaje Hero */}
              <div className="space-y-2">
                <Label className="text-muted-foreground ml-1 text-[9px] font-black tracking-widest uppercase">
                  Mensaje de Bienvenida (WhatsApp Hero)
                </Label>
                <Textarea
                  {...register('contact.heroWhatsappMessage')}
                  className="bg-secondary/30 min-h-[80px] rounded-2xl border-none text-sm font-medium"
                  placeholder="Ej: ¡Hola!"
                />
                <p className="ml-1 text-[8px] font-bold text-zinc-400 uppercase italic">
                  Mensaje pre-llenado cuando tocan el botón principal de tu perfil.
                </p>
              </div>

              {/* Instagram y TikTok (2 Columnas) */}
              <div className="grid grid-cols-2 gap-3">
                <div className="group relative">
                  {!imgErrors.instagram ? (
                    <img
                      src={SocialMediaIcons.instagram}
                      alt="Instagram"
                      onError={() => handleImgError('instagram')}
                      className="absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2"
                    />
                  ) : (
                    <Instagram className="absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2 text-pink-500" />
                  )}
                  <Input
                    {...register('contact.instagram', {
                      onChange: (e) => {
                        const cleaned = extractUsername(
                          e.target.value,
                          'instagram',
                        );
                        setValue('contact.instagram', cleaned);
                      },
                    })}
                    className="bg-secondary/30 h-14 rounded-2xl border-none pl-11 text-xs font-bold"
                    placeholder="@Instagram"
                  />
                </div>
                <div className="group relative">
                  {!imgErrors.tiktok ? (
                    <img
                      src={SocialMediaIcons.tiktok}
                      alt="TikTok"
                      onError={() => handleImgError('tiktok')}
                      className="absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2"
                    />
                  ) : (
                    <div className="absolute top-1/2 left-4 -translate-y-1/2">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-5 w-5"
                      >
                        <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
                      </svg>
                    </div>
                  )}
                  <Input
                    {...register('contact.tiktok', {
                      onChange: (e) => {
                        const cleaned = extractUsername(
                          e.target.value,
                          'tiktok',
                        );
                        setValue('contact.tiktok', cleaned);
                      },
                    })}
                    className="bg-secondary/30 h-14 rounded-2xl border-none pl-11 text-xs font-bold"
                    placeholder="@TikTok"
                  />
                </div>
              </div>

              {/* Facebook */}
              <div className="group relative">
                {!imgErrors.facebook ? (
                  <img
                    src={SocialMediaIcons.facebook}
                    alt="Facebook"
                    onError={() => handleImgError('facebook')}
                    className="absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2"
                  />
                ) : (
                  <Facebook className="absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2 text-blue-600" />
                )}
                <Input
                  {...register('contact.facebook')}
                  className="bg-secondary/30 h-14 rounded-2xl border-none pl-12 text-sm font-bold"
                  placeholder="Link completo de Facebook"
                />
              </div>

              {/* Ubicación y Maps */}
              <div className="space-y-3 pt-2">
                <div className="space-y-2">
                  <Label className="text-muted-foreground ml-1 text-[9px] font-black tracking-widest uppercase">
                    Dirección Física
                  </Label>
                  <div className="relative">
                    <MapPin className="text-muted-foreground absolute top-4 left-4 h-5 w-5 text-rose-500" />
                    <Textarea
                      {...register('contact.address')}
                      className="bg-secondary/30 min-h-[80px] rounded-2xl border-none pt-4 pl-12 text-sm leading-relaxed font-medium"
                      placeholder="Av. Principal, Edificio Central, Local 1..."
                    />
                  </div>
                </div>
                <div className="group relative">
                  {!imgErrors.google_maps ? (
                    <img
                      src={SocialMediaIcons.google_maps}
                      alt="Google Maps"
                      onError={() => handleImgError('google_maps')}
                      className="absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2"
                    />
                  ) : (
                    <Navigation className="absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2 text-blue-500" />
                  )}
                  <Input
                    {...register('contact.google_maps')}
                    className="bg-secondary/30 h-14 rounded-2xl border-none pl-12 text-sm font-bold"
                    placeholder="Link de Google Maps (Opcional)"
                  />
                </div>
              </div>
            </div>

            <Separator className="opacity-50" />

            {/* Color de Marca */}
            <div className="bg-secondary/30 flex items-center justify-between rounded-2xl p-4">
              <div className="space-y-0.5">
                <Label className="text-foreground text-[10px] font-black tracking-widest uppercase">
                  Color del Negocio
                </Label>
                <p className="text-muted-foreground text-[9px] font-medium">
                  Define el color de tus botones.
                </p>
              </div>
              <div className="border-background relative h-12 w-12 overflow-hidden rounded-full border-4 shadow-lg">
                <Controller
                  control={control}
                  name="styles.primary"
                  render={({ field }) => (
                    <Input
                      type="color"
                      {...field}
                      value={field.value || '#0F172A'}
                      className="absolute -top-4 -left-4 h-20 w-20 cursor-pointer border-none bg-transparent p-0"
                    />
                  )}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      <ImageCropperModal
        isOpen={cropper.isOpen}
        imageSrc={cropper.imageSrc}
        aspectRatio={cropper.aspectRatio}
        onCropComplete={onCropComplete}
        onClose={() => setCropper((prev) => ({ ...prev, isOpen: false }))}
      />

      <FormGuard
        isDirty={hasChanges}
        isSubmitting={profileMutation.isPending || slugMutation.isPending}
        onSave={handleSaveAll}
        saveLabel="Publicar Cambios"
      />
    </div>
  );
}